import React, { useState, useRef, useCallback, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Editor from '@monaco-editor/react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { motion, AnimatePresence } from 'framer-motion'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import {
  Save,
  Eye,
  EyeOff,
  Smartphone,
  Tablet,
  Monitor,
  Globe,
  ArrowLeft,
  Code2,
  RefreshCw,
  ExternalLink,
  Loader2,
  FileCode,
  Palette,
  Search,
  Clock,
  ChevronLeft,
  Plus,
  Trash2,
  X,
  Home,
  CheckCircle,
  Image,
  Copy,
  Braces,
} from 'lucide-react'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card'
import LoadingSpinner from '../common/LoadingSpinner'
import { cn } from '../../utils/cn'
import { useTheme } from '../ui/theme-provider'
import { INDUSTRIES } from '../../utils/industrySchemas'

const DEVICE_SIZES = {
  desktop: { width: '100%',   label: 'Desktop',  icon: Monitor },
  tablet:  { width: '768px',  label: 'Tablet',   icon: Tablet },
  mobile:  { width: '375px',  label: 'Mobile',   icon: Smartphone },
}

const CODE_TABS = [
  { id: 'html', label: 'HTML', language: 'html',       icon: FileCode },
  { id: 'css',  label: 'CSS',  language: 'css',        icon: Palette },
  { id: 'js',   label: 'JS',   language: 'javascript', icon: Code2 },
]

const CodeEditor = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { theme } = useTheme()

  const siteId   = searchParams.get('site')
  const clientId = searchParams.get('client')

  const [activeTab,      setActiveTab]      = useState('html')
  const [device,         setDevice]         = useState('desktop')
  const [showPreview,    setShowPreview]    = useState(true)
  const [liveRefresh,    setLiveRefresh]    = useState(false)
  const [hasUnsaved,     setHasUnsaved]     = useState(false)
  const [isPublishing,   setIsPublishing]   = useState(false)
  const [previewKey,     setPreviewKey]     = useState(0)
  const [publicUrl,      setPublicUrl]      = useState(null)

  // Pages state
  const [pages,          setPages]          = useState([])
  const [activePage,     setActivePage]     = useState(null)
  const [showAddPage,    setShowAddPage]    = useState(false)
  const [newPageName,    setNewPageName]    = useState('')
  const [newPageSlug,    setNewPageSlug]    = useState('')
  const [deletingPageId, setDeletingPageId] = useState(null)

  // Media picker state
  const [showMediaPicker, setShowMediaPicker] = useState(false)

  // Brand palette state
  const [showBrandPalette, setShowBrandPalette] = useState(false)

  // Variables panel state
  const [showVarsPanel, setShowVarsPanel] = useState(false)
  const [varsTab,       setVarsTab]       = useState(null)

  const editorRefs = useRef({})

  // Site search state
  const [searchQuery,    setSearchQuery]    = useState('')
  const [filteredSites,  setFilteredSites]  = useState([])

  const [code, setCode] = useState({ html: '', css: '', js: '' })
  const codeRef = useRef({ html: '', css: '', js: '' })
  const iframeRef = useRef(null)

  // ── Site search query (only when no ?site=) ──────────────────────────────
  const { data: allSites = [] } = useQuery(
    'all-sites',
    () => apiService.get('/sites').then(r => r.data.sites || []),
    { enabled: !siteId }
  )

  useEffect(() => {
    if (!siteId && allSites.length > 0 && filteredSites.length === 0 && searchQuery === '') {
      setFilteredSites(
        [...allSites]
          .filter(s => s.updated_at)
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
          .slice(0, 3)
      )
    }
  }, [allSites, siteId])

  const handleSearchChange = (e) => {
    const q = e.target.value
    setSearchQuery(q)
    if (q.trim() === '') {
      setFilteredSites(
        [...allSites]
          .filter(s => s.updated_at)
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
          .slice(0, 3)
      )
    } else {
      setFilteredSites(
        allSites.filter(s =>
          s.name.toLowerCase().includes(q.toLowerCase()) ||
          (s.company_name || '').toLowerCase().includes(q.toLowerCase())
        )
      )
    }
  }

  const handleSiteSelect = (s) => {
    navigate(`/admin/editor?site=${s.id}&client=${s.client_id}`)
  }

  // ── Load site ─────────────────────────────────────────────────────────────
  const { data: site, isLoading: siteLoading } = useQuery(
    ['site', siteId],
    () => apiService.get(`/sites/${siteId}`).then(r => r.data),
    { enabled: !!siteId }
  )

  // ── Reset editor state when the site changes ─────────────────────────────
  useEffect(() => {
    setActivePage(null)
    setPages([])
    setCode({ html: '', css: '', js: '' })
    codeRef.current = { html: '', css: '', js: '' }
    setHasUnsaved(false)
    setPublicUrl(null)
  }, [siteId])

  // ── Load client media ─────────────────────────────────────────────────────
  const { data: mediaItems = [] } = useQuery(
    ['media', clientId],
    () => apiService.get('/media', { params: { clientId } }).then(r => r.data.media || []),
    { enabled: !!clientId && showMediaPicker }
  )

  // ── Load pages ────────────────────────────────────────────────────────────
  const { data: pagesData, isLoading: pagesLoading, refetch: refetchPages } = useQuery(
    ['pages', siteId],
    () => apiService.get(`/sites/${siteId}/pages`).then(r => r.data.pages || []),
    { enabled: !!siteId }
  )

  // Load home page whenever pages data arrives (covers cache hits too)
  useEffect(() => {
    if (!pagesData || pagesData.length === 0) return
    setPages(pagesData)
    // Only auto-load if no page is active yet (don't override mid-session)
    setActivePage(prev => {
      if (prev) return prev
      const home = pagesData.find(p => p.is_home) || pagesData[0]
      // Only load HTML — CSS/JS come from the site-level effect below
      setCode(prev2 => ({ ...prev2, html: home.html_content || '' }))
      codeRef.current = { ...codeRef.current, html: home.html_content || '' }
      setPreviewKey(k => k + 1)
      return home
    })
  }, [pagesData])

  // Load site-level CSS/JS whenever the site changes (global styles for all pages)
  useEffect(() => {
    if (!site) return
    const siteCss = site.draft_css_content || site.css_content || ''
    const siteJs  = site.draft_js_content  || site.js_content  || ''
    setCode(prev => ({ ...prev, css: siteCss, js: siteJs }))
    codeRef.current = { ...codeRef.current, css: siteCss, js: siteJs }
  }, [site?.id])

  // Load a page — only updates HTML; CSS/JS remain site-level
  const loadPage = (page) => {
    setActivePage(page)
    setCode(prev => ({ ...prev, html: page.html_content || '' }))
    codeRef.current = { ...codeRef.current, html: page.html_content || '' }
    setHasUnsaved(false)
    setPreviewKey(k => k + 1)
  }

  // ── Page-level save mutation (HTML only) ─────────────────────────────────
  const savePageMutation = useMutation(
    ({ pageId, payload }) => apiService.put(`/sites/${siteId}/pages/${pageId}`, payload),
    {
      onSuccess: () => {
        toast.success('Saved')
        setHasUnsaved(false)
        refetchPages()
      },
      onError: () => toast.error('Failed to save page'),
    }
  )

  // ── Site-level save mutation (CSS + JS global) ────────────────────────────
  const saveSiteMutation = useMutation(
    (payload) => apiService.put(`/sites/${siteId}`, payload),
    {
      onError: () => toast.error('Failed to save global CSS/JS'),
    }
  )

  // ── Add page mutation ─────────────────────────────────────────────────────
  const addPageMutation = useMutation(
    (payload) => apiService.post(`/sites/${siteId}/pages`, payload),
    {
      onSuccess: (res) => {
        toast.success(`Page "${res.data.page.name}" created`)
        setShowAddPage(false)
        setNewPageName('')
        setNewPageSlug('')
        refetchPages().then(({ data }) => {
          const created = data?.pages?.find(p => p.id === res.data.page.id)
          if (created) loadPage(created)
        })
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Failed to create page')
      },
    }
  )

  // ── Delete page mutation ──────────────────────────────────────────────────
  const deletePageMutation = useMutation(
    (pageId) => apiService.delete(`/sites/${siteId}/pages/${pageId}`),
    {
      onSuccess: (_, deletedId) => {
        toast.success('Page deleted')
        setDeletingPageId(null)
        refetchPages().then(({ data }) => {
          const remaining = data?.pages || []
          if (activePage?.id === deletedId && remaining.length > 0) {
            loadPage(remaining.find(p => p.is_home) || remaining[0])
          }
        })
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Failed to delete page')
        setDeletingPageId(null)
      },
    }
  )

  // ── Publish mutation ──────────────────────────────────────────────────────
  const publishMutation = useMutation(
    () => apiService.post(`/sites/${siteId}/publish`),
    {
      onSuccess: (res) => {
        toast.success('Site published successfully!')
        setIsPublishing(false)
        if (res.data?.public_url) setPublicUrl(res.data.public_url)
        queryClient.invalidateQueries(['site', siteId])
      },
      onError: () => {
        toast.error('Failed to publish site')
        setIsPublishing(false)
      },
    }
  )

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleCodeChange = useCallback((value, lang) => {
    codeRef.current = { ...codeRef.current, [lang]: value || '' }
    setCode(prev => ({ ...prev, [lang]: value || '' }))
    setHasUnsaved(true)
    if (liveRefresh) setPreviewKey(k => k + 1)
  }, [liveRefresh])

  const refreshPreview = useCallback(() => setPreviewKey(k => k + 1), [])

  const handleSave = useCallback(() => {
    // Save CSS/JS globally to the site (applies to all pages)
    saveSiteMutation.mutate({
      css_content: codeRef.current.css,
      js_content:  codeRef.current.js,
    })
    // Save HTML to the current page
    if (activePage) {
      savePageMutation.mutate({
        pageId: activePage.id,
        payload: { html_content: codeRef.current.html },
      })
    }
  }, [activePage])

  const handlePageSwitch = useCallback((page) => {
    if (page.id === activePage?.id) return
    if (hasUnsaved && activePage) {
      // Auto-save HTML to the page being left
      savePageMutation.mutate({
        pageId: activePage.id,
        payload: { html_content: codeRef.current.html },
      })
      // Auto-save CSS/JS to the site (global)
      saveSiteMutation.mutate({
        css_content: codeRef.current.css,
        js_content:  codeRef.current.js,
      })
    }
    loadPage(page)
  }, [activePage, hasUnsaved])

  const handlePublish = useCallback(async () => {
    setIsPublishing(true)
    // Always save CSS/JS to site before publishing so they get promoted to published state
    const savePromises = [
      saveSiteMutation.mutateAsync({
        css_content: codeRef.current.css,
        js_content:  codeRef.current.js,
      }),
    ]
    if (activePage) {
      savePromises.push(savePageMutation.mutateAsync({
        pageId: activePage.id,
        payload: { html_content: codeRef.current.html },
      }))
    }
    await Promise.all(savePromises).catch(() => {})
    publishMutation.mutate()
  }, [hasUnsaved, activePage])

  const handleAddPage = () => {
    if (!newPageName.trim()) return
    const slug = newPageSlug.trim() || `/${newPageName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/, '')}`
    addPageMutation.mutate({ name: newPageName.trim(), slug })
  }

  // ── Insert raw text at cursor (used by Variables panel) ──────────────────
  const insertTextAtCursor = useCallback((text) => {
    const editor = editorRefs.current[activeTab]
    if (editor) {
      const selection = editor.getSelection()
      editor.executeEdits('insert-var', [{ range: selection, text, forceMoveMarkers: true }])
      editor.focus()
    } else {
      navigator.clipboard.writeText(text)
      toast.success('Copied to clipboard')
    }
  }, [activeTab])

  // ── Insert media URL at cursor ────────────────────────────────────────────
  const insertMediaAtCursor = useCallback((url) => {
    const editor = editorRefs.current[activeTab]
    if (!editor) {
      navigator.clipboard.writeText(url)
      toast.success('URL copied to clipboard')
      setShowMediaPicker(false)
      return
    }
    const selection = editor.getSelection()
    const text = activeTab === 'html'
      ? `<img src="${url}" alt="" />`
      : activeTab === 'css'
      ? `url('${url}')`
      : `"${url}"`
    editor.executeEdits('media-picker', [{ range: selection, text, forceMoveMarkers: true }])
    editor.focus()
    setShowMediaPicker(false)
  }, [activeTab])

  // Auto-generate slug from name
  const handlePageNameChange = (e) => {
    const name = e.target.value
    setNewPageName(name)
    setNewPageSlug(`/${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/, '')}`)
  }

  // ── PostMessage: iframe link interception ─────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type !== 'navigate') return
      const slug = e.data.slug
      const target = pages.find(p => p.slug === slug || p.slug === `/${slug.replace(/^\//, '')}`)
      if (target) {
        handlePageSwitch(target)
      } else {
        toast(`Page "${slug}" not found in this site`, { icon: '🔗' })
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [pages, handlePageSwitch])

  // ── Keyboard shortcut: Cmd/Ctrl+S ─────────────────────────────────────────
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        handleSave()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [handleSave])

  // ── Build preview document ─────────────────────────────────────────────────
  // Mirror exactly how customDomainRouter injects CSS/JS into the live site:
  // inject into the existing HTML structure rather than wrapping it, so that
  // code.js doesn't end up after </html> and get silently ignored by the browser.
  const buildPreviewDoc = () => {
    let doc = code.html || '<!DOCTYPE html><html><head></head><body></body></html>'

    // Prevent scroll chaining: overscroll-behavior:none stops the iframe from
    // propagating scrollIntoView / wheel events up to the parent editor page.
    const cspAndCss = `  <meta http-equiv="Content-Security-Policy" content="default-src * data: blob: 'unsafe-inline' 'unsafe-eval';">\n  <style>html,body{overscroll-behavior:none;}${code.css}</style>`
    doc = doc.includes('</head>')
      ? doc.replace('</head>', `${cspAndCss}\n</head>`)
      : `<head>${cspAndCss}</head>\n${doc}`

    // Use window.scrollTo() instead of scrollIntoView() to avoid cross-frame scroll
    // chaining: scrollIntoView() bubbles up through nested scroll containers including
    // the parent browsing context, causing the editor page to shift. window.scrollTo()
    // is scoped to the iframe's own window and does not propagate upward.
    const clickInterceptor = `(function(){document.addEventListener('click',function(e){var a=e.target.closest('a[href]');if(!a)return;var href=a.getAttribute('href');if(!href||href.startsWith('mailto:')||href.startsWith('tel:'))return;if(href.startsWith('#')){e.preventDefault();e.stopPropagation();var target=document.querySelector(href);if(target){var top=target.getBoundingClientRect().top+window.pageYOffset;window.scrollTo({top:top,behavior:'smooth'});}return;}if(!href.startsWith('http://')&&!href.startsWith('https://')&&!href.startsWith('//')){e.preventDefault();e.stopPropagation();window.parent.postMessage({type:'navigate',slug:href},'*');}},true);})();`
    const jsBlock = `<script>${clickInterceptor}\n${code.js}<\/script>`
    doc = doc.includes('</body>')
      ? doc.replace('</body>', `${jsBlock}\n</body>`)
      : `${doc}\n${jsBlock}`

    return doc
  }
  const previewDoc = buildPreviewDoc()

  const monacoTheme = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
    ? 'vs-dark'
    : 'light'

  // ── Site selection screen ──────────────────────────────────────────────────
  if (!siteId) {
    return (
      <div className="h-screen flex flex-col bg-surface-50 dark:bg-dark-300 overflow-hidden">
        <div className="bg-white dark:bg-dark-200 border-b border-neutral-200 dark:border-[#2f2b41] px-6 py-4 shrink-0">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/sites')}
              className="text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to Sites
            </Button>
            <div className="h-5 w-px bg-neutral-200 dark:border-[#2f2b41]" />
            <div className="flex items-center gap-2">
              <Code2 className="h-5 w-5 text-accent-600" />
              <h1 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Code Editor</h1>
            </div>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center p-8 overflow-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-2xl"
          >
            <Card>
              <CardHeader className="text-center pb-2">
                <div className="mx-auto mb-4 p-3 bg-accent-50 dark:bg-accent-900/20 rounded-2xl w-fit">
                  <Search className="h-8 w-8 text-accent-600" />
                </div>
                <CardTitle className="text-2xl">Select a Website to Edit</CardTitle>
                <CardDescription className="text-base mt-1">
                  Search and select a site to open in the code editor
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Search by site name or company..."
                    autoFocus
                    className={cn(
                      'w-full pl-10 pr-4 py-3 border border-neutral-200 dark:border-[#2f2b41] rounded-xl',
                      'bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100',
                      'focus:ring-2 focus:ring-accent-500 focus:border-transparent',
                      'placeholder:text-neutral-400 dark:placeholder:text-neutral-500 transition-all'
                    )}
                  />
                </div>
                <AnimatePresence>
                  {filteredSites.length > 0 ? (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      className="border border-neutral-200 dark:border-[#2f2b41] rounded-xl overflow-hidden"
                    >
                      {searchQuery === '' && (
                        <div className="px-4 py-2 bg-neutral-50 dark:bg-dark-400 border-b border-neutral-200 dark:border-[#2f2b41]">
                          <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Recently edited</p>
                        </div>
                      )}
                      {filteredSites.map((s, i) => (
                        <motion.button
                          key={s.id}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.04 }}
                          onClick={() => handleSiteSelect(s)}
                          className={cn(
                            'w-full text-left px-4 py-3.5 flex items-center justify-between',
                            'hover:bg-neutral-50 dark:hover:bg-[#2a2538] transition-colors',
                            i < filteredSites.length - 1 && 'border-b border-neutral-100 dark:border-[#2f2b41]'
                          )}
                        >
                          <div className="min-w-0">
                            <p className="font-medium text-neutral-900 dark:text-neutral-100 truncate">{s.name}</p>
                            <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">{s.company_name}</p>
                            {s.updated_at && (
                              <p className="text-xs text-neutral-400 dark:text-neutral-500 flex items-center gap-1 mt-0.5">
                                <Clock className="h-3 w-3" />
                                Updated {new Date(s.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </p>
                            )}
                          </div>
                          <span className={cn(
                            'ml-4 shrink-0 px-2.5 py-0.5 rounded-full text-xs font-medium',
                            s.status === 'published'
                              ? 'bg-success-100 text-success-700 dark:bg-success-900/20 dark:text-success-300'
                              : 'bg-warning-100 text-warning-700 dark:bg-warning-900/20 dark:text-warning-300'
                          )}>
                            {s.status}
                          </span>
                        </motion.button>
                      ))}
                    </motion.div>
                  ) : searchQuery.trim() !== '' ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-center py-10 text-neutral-500 dark:text-neutral-400"
                    >
                      <Search className="h-8 w-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No sites found for "{searchQuery}"</p>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    )
  }

  if (siteLoading || pagesLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" text="Loading editor..." />
      </div>
    )
  }

  // Site exists but has no pages yet (e.g. created before auto-home-page was added)
  if (!pagesLoading && pages.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen bg-surface-50 dark:bg-dark-300">
        <div className="text-center space-y-4">
          <p className="text-neutral-600 dark:text-neutral-400">This site has no pages yet.</p>
          <Button
            onClick={() => addPageMutation.mutate({ name: 'Home', slug: '/', is_home: true })}
            disabled={addPageMutation.isLoading}
          >
            {addPageMutation.isLoading ? 'Creating...' : 'Create Home Page'}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-surface-50 dark:bg-dark-300 overflow-hidden">

      {/* ── Top Toolbar ── */}
      <div className="border-b border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-dark-200 shrink-0">

        {/* Main row: back + name (left) | Save + Publish always visible (right) */}
        <div className="flex items-center justify-between px-4 py-2">

          {/* Left: back + site name */}
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(clientId ? `/admin/clients/${clientId}` : '/admin/sites')}
              className="shrink-0 text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 leading-none truncate">
                {site?.name || 'Untitled Site'}
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">
                {site?.company_name || ''}
              </p>
            </div>
            {hasUnsaved && (
              <span className="shrink-0 text-xs text-warning-600 dark:text-warning-400 font-medium">• Unsaved</span>
            )}
          </div>

          {/* Right: utility controls (desktop only) + Save + Publish */}
          <div className="flex items-center gap-2 shrink-0">

            {/* Utility controls — hidden on mobile, shown on desktop */}
            <div className="hidden sm:flex items-center gap-2">
              <div className="flex items-center gap-1 border border-neutral-200 dark:border-[#2f2b41] rounded-lg p-1">
                {Object.entries(DEVICE_SIZES).map(([key, { label, icon: Icon }]) => (
                  <button
                    key={key}
                    onClick={() => setDevice(key)}
                    title={label}
                    className={cn(
                      'p-1.5 rounded-md transition-all',
                      device === key
                        ? 'bg-accent-600 text-white'
                        : 'text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200'
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </button>
                ))}
              </div>

              <button
                onClick={() => setLiveRefresh(v => !v)}
                title="Live refresh"
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all',
                  liveRefresh
                    ? 'bg-success-50 border-success-200 text-success-700 dark:bg-success-900/20 dark:border-success-700 dark:text-success-300'
                    : 'border-neutral-200 dark:border-[#2f2b41] text-neutral-500 dark:text-neutral-400 hover:border-neutral-300'
                )}
              >
                <RefreshCw className={cn('h-3 w-3', liveRefresh && 'animate-spin-slow')} />
                Live
              </button>

              <Button variant="ghost" size="icon" onClick={refreshPreview} title="Refresh preview">
                <RefreshCw className="h-4 w-4" />
              </Button>

              <Button variant="ghost" size="icon" onClick={() => setShowPreview(v => !v)} title="Toggle preview">
                {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>

              <Button
                variant="ghost"
                size="icon"
                title="Open preview in new tab"
                onClick={() => {
                  const blob = new Blob([previewDoc], { type: 'text/html' })
                  window.open(URL.createObjectURL(blob), '_blank')
                }}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>

              {/* Live URL — desktop only */}
              {publicUrl && (
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open live site"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-success-200 dark:border-success-700 bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-300 hover:bg-success-100 dark:hover:bg-success-900/40 transition-colors max-w-[180px]"
                >
                  <CheckCircle className="h-3 w-3 shrink-0" />
                  <span className="truncate">{publicUrl.replace('https://', '')}</span>
                </a>
              )}
            </div>

            {/* Save + Publish — always visible */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleSave}
              disabled={savePageMutation.isLoading}
              className="gap-1.5"
            >
              {savePageMutation.isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save
            </Button>

            <Button
              size="sm"
              onClick={handlePublish}
              disabled={isPublishing || publishMutation.isLoading}
              className="gap-1.5"
            >
              {isPublishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Globe className="h-3.5 w-3.5" />}
              Publish
            </Button>
          </div>
        </div>

        {/* Mobile utility row — shown only on small screens */}
        <div className="flex sm:hidden items-center gap-2 px-4 pb-2 overflow-x-auto">
          <div className="flex items-center gap-1 border border-neutral-200 dark:border-[#2f2b41] rounded-lg p-1 shrink-0">
            {Object.entries(DEVICE_SIZES).map(([key, { label, icon: Icon }]) => (
              <button
                key={key}
                onClick={() => setDevice(key)}
                title={label}
                className={cn(
                  'p-1.5 rounded-md transition-all',
                  device === key
                    ? 'bg-accent-600 text-white'
                    : 'text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200'
                )}
              >
                <Icon className="h-3.5 w-3.5" />
              </button>
            ))}
          </div>

          <button
            onClick={() => setLiveRefresh(v => !v)}
            className={cn(
              'shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all',
              liveRefresh
                ? 'bg-success-50 border-success-200 text-success-700 dark:bg-success-900/20 dark:border-success-700 dark:text-success-300'
                : 'border-neutral-200 dark:border-[#2f2b41] text-neutral-500 dark:text-neutral-400'
            )}
          >
            <RefreshCw className={cn('h-3 w-3', liveRefresh && 'animate-spin-slow')} />
            Live
          </button>

          <Button variant="ghost" size="icon" onClick={refreshPreview} title="Refresh preview" className="shrink-0">
            <RefreshCw className="h-4 w-4" />
          </Button>

          <Button variant="ghost" size="icon" onClick={() => setShowPreview(v => !v)} title="Toggle preview" className="shrink-0">
            {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>

          <Button
            variant="ghost"
            size="icon"
            title="Open preview in new tab"
            className="shrink-0"
            onClick={() => {
              const blob = new Blob([previewDoc], { type: 'text/html' })
              window.open(URL.createObjectURL(blob), '_blank')
            }}
          >
            <ExternalLink className="h-4 w-4" />
          </Button>

          {publicUrl && (
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-success-200 dark:border-success-700 bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-300 transition-colors"
            >
              <CheckCircle className="h-3 w-3 shrink-0" />
              <span className="truncate max-w-[120px]">{publicUrl.replace('https://', '')}</span>
            </a>
          )}
        </div>
      </div>

      {/* ── Page tab strip ── */}
      <div className="flex items-center border-b border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-dark-200 px-2 shrink-0 overflow-x-auto">
        {pages.map((page) => (
          <button
            key={page.id}
            onClick={() => handlePageSwitch(page)}
            className={cn(
              'group flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-all -mb-px shrink-0 whitespace-nowrap',
              activePage?.id === page.id
                ? 'border-accent-600 text-accent-600 dark:text-accent-400'
                : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200'
            )}
          >
            {page.is_home ? <Home className="h-3 w-3" /> : null}
            {page.name}
            {!page.is_home && (
              <span
                role="button"
                title="Delete page"
                onClick={(e) => {
                  e.stopPropagation()
                  setDeletingPageId(page.id)
                }}
                className="ml-1 opacity-0 group-hover:opacity-100 hover:text-error-500 transition-all rounded"
              >
                <X className="h-3 w-3" />
              </span>
            )}
          </button>
        ))}

        {/* Add page button */}
        <button
          onClick={() => setShowAddPage(true)}
          title="Add page"
          className="flex items-center gap-1 px-3 py-2 text-xs text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors shrink-0"
        >
          <Plus className="h-3.5 w-3.5" />
          Add page
        </button>
      </div>

      {/* ── Main area ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Editor pane ── */}
        <div className={cn(
          'flex flex-col border-r border-neutral-200 dark:border-[#2f2b41] transition-all',
          showPreview ? 'w-1/2' : 'w-full'
        )}>
          <>
              <div className="flex items-center justify-between border-b border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-dark-200 px-2">
                <div className="flex items-center">
                  {CODE_TABS.map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      onClick={() => setActiveTab(id)}
                      title={id !== 'html' ? 'Global — applies to all pages' : undefined}
                      className={cn(
                        'flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-all -mb-px',
                        activeTab === id
                          ? 'border-accent-600 text-accent-600 dark:text-accent-400'
                          : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200'
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">{label}</span>
                      {id !== 'html' && (
                        <span className="hidden sm:inline text-[10px] font-medium px-1 py-0.5 rounded bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 leading-none">
                          Global
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1">
                  {site?.brand_colors?.length > 0 && (
                    <button
                      onClick={() => setShowBrandPalette(v => !v)}
                      title="Brand palette"
                      className={cn(
                        'flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-all',
                        showBrandPalette
                          ? 'bg-accent-50 dark:bg-accent-900/20 text-accent-600 dark:text-accent-400'
                          : 'text-neutral-500 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 hover:bg-accent-50 dark:hover:bg-accent-900/20'
                      )}
                    >
                      <Palette className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Brand</span>
                    </button>
                  )}
                  {site?.industry && INDUSTRIES[site.industry] && (
                    <button
                      onClick={() => {
                        setShowVarsPanel(v => !v)
                        if (!varsTab) setVarsTab(INDUSTRIES[site.industry].tabs[0]?.id)
                      }}
                      title="Content variables"
                      className={cn(
                        'flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-all',
                        showVarsPanel
                          ? 'bg-accent-50 dark:bg-accent-900/20 text-accent-600 dark:text-accent-400'
                          : 'text-neutral-500 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 hover:bg-accent-50 dark:hover:bg-accent-900/20'
                      )}
                    >
                      <Braces className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Variables</span>
                    </button>
                  )}
                  <button
                    onClick={() => setShowMediaPicker(true)}
                    title="Insert media"
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg text-neutral-500 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 hover:bg-accent-50 dark:hover:bg-accent-900/20 transition-all"
                  >
                    <Image className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Media</span>
                  </button>
                </div>
              </div>

              {/* Variables panel */}
              {showVarsPanel && site?.industry && INDUSTRIES[site.industry] && (() => {
                const schema = INDUSTRIES[site.industry]
                const activeVarsTabSchema = schema.tabs.find(t => t.id === varsTab) || schema.tabs[0]
                return (
                  <div className="border-b border-neutral-200 dark:border-[#2f2b41] bg-neutral-50 dark:bg-dark-400">
                    {/* Tab selector */}
                    <div className="flex items-center gap-1 px-3 pt-2 pb-0 overflow-x-auto">
                      <span className="text-xs font-semibold text-neutral-400 dark:text-neutral-500 shrink-0 mr-1">
                        {schema.icon} {schema.label}
                      </span>
                      {schema.tabs.map(tab => (
                        <button
                          key={tab.id}
                          onClick={() => setVarsTab(tab.id)}
                          className={cn(
                            'shrink-0 px-2.5 py-1 text-xs font-medium rounded-t-md border-b-2 transition-all whitespace-nowrap',
                            (varsTab || schema.tabs[0].id) === tab.id
                              ? 'border-accent-600 text-accent-600 dark:text-accent-400 bg-white dark:bg-dark-200'
                              : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200'
                          )}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>

                    {/* Fields for active tab */}
                    <div className="px-3 py-2.5 space-y-2 max-h-48 overflow-y-auto bg-white dark:bg-dark-200">
                      {activeVarsTabSchema?.fields.map(field => {
                        if (field.type === 'list') {
                          const subKeys = field.subFields.map(sf => `  {{${sf.key}}}`).join('\n')
                          const loopSnippet = `{{#${field.key}}}\n${subKeys}\n{{/${field.key}}}`
                          return (
                            <div key={field.key} className="space-y-1">
                              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">{field.label} <span className="text-neutral-400 dark:text-neutral-500">(repeating list)</span></p>
                              <div className="flex items-start gap-2 bg-neutral-50 dark:bg-dark-400 rounded-lg p-2 border border-neutral-200 dark:border-[#2f2b41]">
                                <pre className="text-xs font-mono text-accent-600 dark:text-accent-400 flex-1 whitespace-pre">{loopSnippet}</pre>
                                <button
                                  onClick={() => insertTextAtCursor(loopSnippet)}
                                  className="shrink-0 p-1 rounded text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                                  title="Insert loop"
                                >
                                  <Copy className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          )
                        }
                        if (field.type === 'multi-image') {
                          const snippet = `{{#${field.key}}}\n  <img src="{{.}}" alt="" />\n{{/${field.key}}}`
                          return (
                            <div key={field.key} className="space-y-1">
                              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">{field.label} <span className="text-neutral-400 dark:text-neutral-500">(image gallery)</span></p>
                              <div className="flex items-start gap-2 bg-neutral-50 dark:bg-dark-400 rounded-lg p-2 border border-neutral-200 dark:border-[#2f2b41]">
                                <pre className="text-xs font-mono text-accent-600 dark:text-accent-400 flex-1 whitespace-pre">{snippet}</pre>
                                <button
                                  onClick={() => insertTextAtCursor(snippet)}
                                  className="shrink-0 p-1 rounded text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                                  title="Insert"
                                >
                                  <Copy className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          )
                        }
                        if (field.type === 'hours') {
                          const snippet = `{{#hours}}\n  <p>{{day}}: {{openTime}}–{{closeTime}}</p>\n{{/hours}}`
                          return (
                            <div key={field.key} className="space-y-1">
                              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Opening Hours <span className="text-neutral-400 dark:text-neutral-500">(days loop)</span></p>
                              <div className="flex items-start gap-2 bg-neutral-50 dark:bg-dark-400 rounded-lg p-2 border border-neutral-200 dark:border-[#2f2b41]">
                                <pre className="text-xs font-mono text-accent-600 dark:text-accent-400 flex-1 whitespace-pre">{snippet}</pre>
                                <button
                                  onClick={() => insertTextAtCursor(snippet)}
                                  className="shrink-0 p-1 rounded text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                                  title="Insert"
                                >
                                  <Copy className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          )
                        }
                        // Simple fields: text, textarea, image, url
                        const token = `{{${field.key}}}`
                        return (
                          <button
                            key={field.key}
                            onClick={() => insertTextAtCursor(token)}
                            title={`Insert ${token}`}
                            className="inline-flex items-center gap-1 px-2 py-1 mr-1.5 mb-1 rounded-md bg-neutral-100 dark:bg-dark-400 border border-neutral-200 dark:border-[#2f2b41] hover:border-accent-400 dark:hover:border-accent-500 hover:bg-accent-50 dark:hover:bg-accent-900/20 transition-all group"
                          >
                            <span className="text-xs font-mono text-accent-600 dark:text-accent-400">{token}</span>
                            <span className="text-xs text-neutral-400 dark:text-neutral-500 group-hover:text-neutral-600 dark:group-hover:text-neutral-300">
                              {field.label}
                            </span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )
              })()}

              {/* Brand palette strip */}
              {showBrandPalette && site?.brand_colors?.length > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 border-b border-neutral-200 dark:border-[#2f2b41] bg-neutral-50 dark:bg-dark-400 flex-wrap">
                  <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 shrink-0">
                    Brand Colors
                  </span>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {site.brand_colors.map((color) => (
                      <button
                        key={color}
                        title={`Copy ${color}`}
                        onClick={() => {
                          navigator.clipboard.writeText(color)
                          toast.success(`${color} copied`)
                        }}
                        className="flex items-center gap-1.5 px-2 py-1 rounded-md border border-neutral-200 dark:border-[#2f2b41] hover:border-neutral-400 dark:hover:border-neutral-500 bg-white dark:bg-dark-200 transition-all group"
                      >
                        <span
                          className="w-3.5 h-3.5 rounded-sm shrink-0 border border-black/10"
                          style={{ backgroundColor: color }}
                        />
                        <span className="text-xs font-mono text-neutral-600 dark:text-neutral-300 group-hover:text-neutral-900 dark:group-hover:text-neutral-100">
                          {color}
                        </span>
                        <Copy className="h-2.5 w-2.5 text-neutral-300 dark:text-neutral-600 group-hover:text-neutral-500 dark:group-hover:text-neutral-400 shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex-1 overflow-hidden relative">
                {CODE_TABS.map(({ id, language }) => (
                  <div key={id} className={cn('absolute inset-0', activeTab !== id && 'invisible pointer-events-none')}>
                    <Editor
                      height="100%"
                      language={language}
                      value={code[id]}
                      theme={monacoTheme}
                      onChange={(val) => handleCodeChange(val, id)}
                      onMount={(editor) => { editorRefs.current[id] = editor }}
                      options={{
                        fontSize: 13,
                        fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                        minimap: { enabled: false },
                        scrollBeyondLastLine: false,
                        wordWrap: 'on',
                        lineNumbers: 'on',
                        folding: true,
                        automaticLayout: true,
                        tabSize: 2,
                        padding: { top: 12, bottom: 12 },
                      }}
                    />
                  </div>
                ))}
              </div>
            </>
        </div>

        {/* ── Preview pane ── */}
        {showPreview && (
          <div className="flex-1 flex flex-col bg-neutral-100 dark:bg-dark-400 overflow-hidden">
            <div className="flex items-center justify-between py-2 px-4 bg-white dark:bg-dark-200 border-b border-neutral-200 dark:border-[#2f2b41]">
              <span className="text-xs text-neutral-400 dark:text-neutral-500 font-medium">
                Preview — {activePage?.name || 'Home'} · {DEVICE_SIZES[device].label}
              </span>
              {activePage && (
                <span className="text-xs text-neutral-400 dark:text-neutral-500 font-mono">
                  {activePage.slug}
                </span>
              )}
            </div>
            <div className="flex-1 flex items-start justify-center overflow-x-auto overflow-y-hidden overscroll-contain p-4">
              <div
                style={{ width: DEVICE_SIZES[device].width, maxWidth: '100%' }}
                className="h-full bg-white shadow-lg rounded-lg overflow-hidden transition-all duration-300"
              >
                <iframe
                  key={previewKey}
                  ref={iframeRef}
                  srcDoc={previewDoc}
                  title="Site Preview"
                  className="w-full h-full border-0"
                  sandbox="allow-scripts allow-forms allow-popups"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Add page modal ── */}
      <AnimatePresence>
        {showAddPage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
            onClick={() => setShowAddPage(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.15 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-dark-200 rounded-2xl shadow-large border border-neutral-200 dark:border-[#2f2b41] p-6 w-full max-w-sm mx-4"
            >
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">Add New Page</h3>
                <button
                  onClick={() => setShowAddPage(false)}
                  className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
                    Page name
                  </label>
                  <input
                    type="text"
                    value={newPageName}
                    onChange={handlePageNameChange}
                    placeholder="e.g. Menu, About, Contact"
                    autoFocus
                    className={cn(
                      'w-full px-3 py-2.5 border border-neutral-200 dark:border-[#2f2b41] rounded-xl text-sm',
                      'bg-surface-100 dark:bg-dark-300 text-neutral-900 dark:text-neutral-100',
                      'focus:ring-2 focus:ring-accent-500 focus:border-transparent outline-none transition-all',
                      'placeholder:text-neutral-400 dark:placeholder:text-neutral-500'
                    )}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddPage()}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
                    URL slug
                  </label>
                  <input
                    type="text"
                    value={newPageSlug}
                    onChange={(e) => setNewPageSlug(e.target.value)}
                    placeholder="/menu"
                    className={cn(
                      'w-full px-3 py-2.5 border border-neutral-200 dark:border-[#2f2b41] rounded-xl text-sm font-mono',
                      'bg-surface-100 dark:bg-dark-300 text-neutral-900 dark:text-neutral-100',
                      'focus:ring-2 focus:ring-accent-500 focus:border-transparent outline-none transition-all',
                      'placeholder:text-neutral-400 dark:placeholder:text-neutral-500'
                    )}
                  />
                  <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
                    Links to this slug in your HTML will switch to this page in the preview
                  </p>
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowAddPage(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleAddPage}
                  disabled={!newPageName.trim() || addPageMutation.isLoading}
                >
                  {addPageMutation.isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Plus className="h-3.5 w-3.5 mr-1.5" />}
                  Create page
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Delete confirmation modal ── */}
      <AnimatePresence>
        {deletingPageId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
            onClick={() => setDeletingPageId(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-dark-200 rounded-2xl shadow-large border border-neutral-200 dark:border-[#2f2b41] p-6 w-full max-w-sm mx-4"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-error-50 dark:bg-error-900/20 rounded-xl">
                  <Trash2 className="h-5 w-5 text-error-600" />
                </div>
                <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">Delete page?</h3>
              </div>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-5">
                "{pages.find(p => p.id === deletingPageId)?.name}" will be permanently deleted. This cannot be undone.
              </p>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setDeletingPageId(null)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => deletePageMutation.mutate(deletingPageId)}
                  disabled={deletePageMutation.isLoading}
                >
                  {deletePageMutation.isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Trash2 className="h-3.5 w-3.5 mr-1.5" />}
                  Delete
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Media picker modal ── */}
      <AnimatePresence>
        {showMediaPicker && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
            onClick={() => setShowMediaPicker(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.15 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-dark-200 rounded-2xl shadow-large border border-neutral-200 dark:border-[#2f2b41] p-6 w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col"
            >
              <div className="flex items-center justify-between mb-4 shrink-0">
                <div>
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">Media Library</h3>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">Click an image to insert it into your {activeTab.toUpperCase()}</p>
                </div>
                <button onClick={() => setShowMediaPicker(false)} className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto">
                {mediaItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-neutral-400 dark:text-neutral-500">
                    <Image className="h-10 w-10 mb-3 opacity-30" />
                    <p className="text-sm">No media uploaded for this client yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    {mediaItems.map((item) => {
                      const url = item.public_url || item.file_path || ''
                      return (
                        <button
                          key={item.id}
                          onClick={() => insertMediaAtCursor(url)}
                          className="group relative rounded-xl overflow-hidden border border-neutral-200 dark:border-[#2f2b41] hover:border-accent-500 transition-all aspect-square bg-neutral-100 dark:bg-dark-400"
                        >
                          <img
                            src={item.thumbnail_url || url}
                            alt={item.original_name}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                            <span className="opacity-0 group-hover:opacity-100 text-white text-xs font-medium px-2 py-1 bg-accent-600 rounded-lg transition-all">
                              Insert
                            </span>
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-all">
                            <p className="text-white text-xs truncate">{item.original_name}</p>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-[#2f2b41] shrink-0">
                <p className="text-xs text-neutral-400 dark:text-neutral-500">
                  In HTML: inserts <code className="bg-neutral-100 dark:bg-dark-400 px-1 rounded">&lt;img src="..."&gt;</code> · In CSS: inserts <code className="bg-neutral-100 dark:bg-dark-400 px-1 rounded">url('...')</code> · In JS: inserts the URL string
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}


export default CodeEditor
